import { array, object, optional, string } from 'zod';

import {
  countQuizText,
  hasFormulaOverLength,
  ITEM_FORMULA_LATEX_MAX_LENGTH,
} from '@/shared/features/rich-text/utils/countQuizText';

import { hasQuizItemContent } from './quizItemContent';

export const MIN_MATCH_PAIR_COUNT = 2;
export const MAX_MATCH_PAIR_COUNT = 5;

export const MATCH_ITEM_CONTENT_REQUIRED_MESSAGE =
  'Match item text, equation or image is required';
export const MATCH_ITEM_TEXT_MAX_LENGTH = 50;
const MATCH_PAIR_SEPARATOR = ':';

const MatchItemIdSchema = string()
  .min(1, 'Match item id is required')
  .refine(id => !id.includes(MATCH_PAIR_SEPARATOR), {
    message: 'Match item id cannot contain the pair separator',
  });

const MatchItemSchema = object({
  id: MatchItemIdSchema,
  text: string().refine(
    text => countQuizText(text) <= MATCH_ITEM_TEXT_MAX_LENGTH,
    'Match item text must be at most 50 characters',
  ),
  richText: optional(string()),
  equation: optional(string()),
  imageUrl: optional(string()),
})
  .refine(hasQuizItemContent, {
    message: MATCH_ITEM_CONTENT_REQUIRED_MESSAGE,
  })
  .refine(item => !hasFormulaOverLength(item, ITEM_FORMULA_LATEX_MAX_LENGTH), {
    message: 'Match item formula must be at most 100 characters',
    path: ['text'],
  });

const AnswerPairSchema = object({
  leftId: MatchItemIdSchema,
  rightId: MatchItemIdSchema,
});

export const QuizMatchDataSchema = object({
  leftItems: array(MatchItemSchema)
    .min(MIN_MATCH_PAIR_COUNT)
    .max(MAX_MATCH_PAIR_COUNT),
  rightItems: array(MatchItemSchema)
    .min(MIN_MATCH_PAIR_COUNT)
    .max(MAX_MATCH_PAIR_COUNT),
  answerPairs: array(AnswerPairSchema)
    .min(MIN_MATCH_PAIR_COUNT)
    .max(MAX_MATCH_PAIR_COUNT),
}).refine(
  data => {
    if (
      data.leftItems.length !== data.rightItems.length ||
      data.leftItems.length !== data.answerPairs.length
    ) {
      return false;
    }

    const leftIds = new Set(data.leftItems.map(item => item.id));
    const rightIds = new Set(data.rightItems.map(item => item.id));
    const allItemIds = new Set([...leftIds, ...rightIds]);
    const pairedLeftIds = new Set(data.answerPairs.map(pair => pair.leftId));
    const pairedRightIds = new Set(data.answerPairs.map(pair => pair.rightId));

    return (
      leftIds.size === data.leftItems.length &&
      rightIds.size === data.rightItems.length &&
      allItemIds.size === data.leftItems.length + data.rightItems.length &&
      pairedLeftIds.size === leftIds.size &&
      pairedRightIds.size === rightIds.size &&
      data.answerPairs.every(
        pair => leftIds.has(pair.leftId) && rightIds.has(pair.rightId),
      )
    );
  },
  {
    message: 'Every match item must be paired exactly once',
    path: ['answerPairs'],
  },
);
